import { memo, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { IPromotion } from 'interfaces/IPromotions';
import {
  fetchPromotion,
  fetchPromotionVoyages,
  resetSearchParams,
} from 'store/actions';
import { BodyText, Header2, Header3 } from 'components/UI/Brand/text';
import translate from 'translate';
import PromotionsHeader from 'components/promotions/PromotionsHeader';
import PromotionIncludedSailings from 'components/promotions/PromotionIncludedSailings';
import { getCoastalQuery, getExpeditionQuery } from 'utils/search';
import LoadingWrapper from 'components/LoadingWrapper';
import {
  fetchPromotionVoyagesDestinations,
  resetPromotions,
} from 'store/actions/promotion';
import { fetchTourType, resetTour } from 'store/actions/tour';
import { useAppDispatch, useAppSelector } from 'hooks/storeHooks';
import { searchApi } from 'store/services/search';
import { ISearchDates } from 'interfaces/ISearchDates';
import { ContentfulRenderer } from 'components';
import styles from './styles.module.scss';

const PromotionPage = () => {
  const navigate = useNavigate();
  const [promotion, setPromotion] = useState<IPromotion>(null);
  const { promotions, loading, includedSailingsLoading } = useAppSelector(
    (state) => state.promotions
  );
  const promotionsMap = new Map<string, IPromotion>(promotions);
  const { types } = useAppSelector((state) => state.tour);
  const initialAmountOfVoyagesDisplayed = 10;
  const dispatch = useAppDispatch();
  const params = useParams();
  const [offset, setOffset] = useState<number>(initialAmountOfVoyagesDisplayed);
  const [selectedDestination, setSelectedDestination] = useState<string>(null);
  const isCoastal = promotion?.product === 'Coastal express';

  const [getPackageList, { isLoading: isDateLoading }] =
    searchApi.useGetPackageListMutation();

  const onVoyageClick = async (id: string) => {
    const {
      voyageId,
      fromPort,
      toPort,
      isViaKirkenes,
      destinationId,
      travelSuggestionCodes,
    } = promotion.includedSailings.voyages.find(
      (voyage) => voyage.voyageId === id
    );

    const { startSailingPeriod, cabins } = promotion;

    dispatch(resetSearchParams());

    let searchUrl = '';

    if (!isCoastal) {
      searchUrl = getExpeditionQuery(
        destinationId,
        promotion.endSailingPeriod,
        promotion.startSailingPeriod,
        voyageId,
        cabins
      );
    } else {
      const coastalType = types?.find((type) => type.id === id)?.type || '';
      let firstAvailableSailingDate = startSailingPeriod;
      if (!coastalType) {
        navigate(`/book/coastal`);
        return;
      }

      if (travelSuggestionCodes?.length) {
        try {
          const result = await getPackageList(travelSuggestionCodes.join(','));
          const searchData = (result as { data?: ISearchDates }).data;
          if (searchData) {
            const { dates } = searchData;

            if (dates && dates.length > 0) {
              const sortedDates = dates
                .slice()
                .sort(
                  (a: string, b: string) =>
                    new Date(a).getTime() - new Date(b).getTime()
                );

              // add check about clothest date to promotion.startSailingPeriod
              firstAvailableSailingDate = sortedDates.reduce(
                (closestDate: string, currentDate: string) => {
                  const closestDateDiff = Math.abs(
                    new Date(closestDate).getTime() -
                      new Date(promotion.startSailingPeriod).getTime()
                  );
                  const currentDateDiff = Math.abs(
                    new Date(currentDate).getTime() -
                      new Date(promotion.startSailingPeriod).getTime()
                  );
                  return currentDateDiff < closestDateDiff
                    ? currentDate
                    : closestDate;
                },
                sortedDates[0]
              );
            }
          }
        } catch (error) {
          throw Error(translate('MyBookings_Error'));
        }
      }

      searchUrl = getCoastalQuery(
        coastalType,
        firstAvailableSailingDate,
        voyageId,
        fromPort?.code,
        toPort?.code,
        isViaKirkenes,
        cabins
      );
    }

    navigate(`/book/${searchUrl}`);
  };

  const onLoadMorePromotionVoyagesClick = () => {
    if (promotion) {
      dispatch(
        fetchPromotionVoyages({
          promotionId: promotion.id,
          limit: 9,
          offset,
          destinationId: selectedDestination,
          internalName:
            selectedDestination !== null
              ? promotion.destinations.find(
                  (destination) => destination.value === selectedDestination
                )?.internalName
              : '',
        })
      );
      setOffset(offset + 9);
    }
  };

  useEffect(() => {
    let currentPromotion;
    if (promotionsMap && promotionsMap.size) {
      currentPromotion = promotionsMap.get(params.id);
    }

    if (!currentPromotion?.startSailingPeriod) {
      dispatch(fetchPromotion(params.id, initialAmountOfVoyagesDisplayed, 0));
    }
    setPromotion(currentPromotion);
  }, [promotions]);

  useEffect(() => {
    if (promotion?.id) {
      dispatch(fetchPromotionVoyagesDestinations(promotion.id));
    }
  }, [promotion?.id]);

  useEffect(() => {
    if (promotion?.includedSailings?.voyages && isCoastal) {
      const query = promotion?.includedSailings.voyages
        .map((voyage) => voyage.voyageId)
        .join(',');
      dispatch(fetchTourType(query));
    }
  }, [promotion?.includedSailings?.voyages?.length]);

  useEffect(
    () => () => {
      dispatch(resetPromotions());
      dispatch(resetTour());
    },
    []
  );

  useEffect(() => {
    if (selectedDestination) {
      onLoadMorePromotionVoyagesClick();
    }
  }, [selectedDestination]);

  return (
    <div className={styles.promotionPage}>
      <LoadingWrapper loading={loading} className={styles.content}>
        <section className={styles.section}>
          <PromotionsHeader
            title={promotion?.title}
            description={promotion?.description}
            product={promotion?.product}
            promotionEndDate={promotion?.promotionEndDate}
            image={promotion?.image}
          />
        </section>
        <section className={styles.section}>
          <PromotionIncludedSailings
            includedSailings={promotion?.includedSailings}
            includedSailingsLoading={includedSailingsLoading}
            isCoastal={isCoastal}
            onVoyageClick={onVoyageClick}
            onLoadMorePromotionVoyagesClick={onLoadMorePromotionVoyagesClick}
            destinations={promotion?.destinations}
            selectedDestination={selectedDestination}
            setSelectedDestination={setSelectedDestination}
            isDateLoading={isDateLoading}
          />
        </section>
        {(promotion?.termsAndConditions ||
          promotion?.termsAndConditionsNew) && (
          <section className={styles.section}>
            <div className={styles.termsAndConditionSection}>
              <Header2 as={Header3}>
                {translate('General_TermsAnsConditions')}
              </Header2>
              {promotion?.termsAndConditionsNew ? (
                <div className={styles.termsAndCondition}>
                  <ContentfulRenderer>
                    {promotion?.termsAndConditionsNew}
                  </ContentfulRenderer>
                </div>
              ) : (
                <BodyText className={styles.termsAndCondition}>
                  {promotion?.termsAndConditions}
                </BodyText>
              )}
            </div>
          </section>
        )}
      </LoadingWrapper>
    </div>
  );
};

export default memo(PromotionPage);
